import {Component, forwardRef, input, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AbstractControl, ControlValueAccessor, FormArray, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, ValidatorFn} from '@angular/forms';
import {ColumnMode, TableColumn} from '@shared/components/ace-table/ace-table.component';
import {ExternalReferenceId, ExternalReferenceRow} from '@shared/services/api/external-reference.service';
import {updateControlEnableState} from '@shared/util/angular/angular-utils';
import {AceValidators} from '@shared/util/validators/ace-validators';

interface ExternalReferenceRowForm {
  id: FormControl<ExternalReferenceId | null>;
  label: FormControl<string>;
  externalReference: FormControl<string>;
}

@Component({
  selector: 'app-external-references',
  templateUrl: './external-references.component.html',
  styleUrl: './external-references.component.scss',
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ExternalReferencesComponent), multi: true},
    {provide: NG_VALIDATORS, useExisting: forwardRef(() => ExternalReferencesComponent), multi: true}
  ],
  standalone: false
})
export class ExternalReferencesComponent implements OnInit, ControlValueAccessor, Validator {
  private static readonly uniqueLabels: ValidatorFn = (formArray: AbstractControl): ValidationErrors | null => {
    const seenLabels = new Set<string>();
    for (const row of (<FormArray<FormGroup<ExternalReferenceRowForm>>>formArray).controls) {
      const label = row.controls.label.value?.trim();
      if (label) {
        const comparableLabel = label.toLocaleLowerCase();
        if (seenLabels.has(comparableLabel)) {
          return {duplicateLabel: {messageKey: 'Duplicate External Reference Label', parameters: {label}}};
        }
        seenLabels.add(comparableLabel);
      }
    }
    return null;
  };

  @ViewChild('headerTemplate', {static: true}) protected headerTemplate: TemplateRef<any>;
  @ViewChild('cellTemplate', {static: true}) protected cellTemplate: TemplateRef<any>;

  readonly isReadonly = input<boolean>(false);

  private onChange: (rows: ExternalReferenceRow[]) => void = _rows => {};

  protected readonly ColumnMode = ColumnMode;
  protected readonly rows = new FormArray<FormGroup<ExternalReferenceRowForm>>(
    [], {validators: [ExternalReferencesComponent.uniqueLabels]});
  protected onTouched: () => void = () => {};
  protected columns: TableColumn[] = [];
  protected tableRows: ExternalReferenceRow[] = [];

  constructor() {
    this.rows.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.tableRows = this.rows.getRawValue();
      this.onChange(this.tableRows);
    });
  }

  ngOnInit(): void {
    const columnDefaults = {headerTemplate: this.headerTemplate, cellTemplate: this.cellTemplate, flexGrow: 1};
    this.columns = [
      {...columnDefaults, name: 'Label', prop: 'label'},
      {...columnDefaults, name: 'External Reference', prop: 'externalReference'}
    ];
  }

  writeValue(rows: ExternalReferenceRow[] | null): void {
    this.rows.clear({emitEvent: false});
    rows?.forEach(row => this.rows.push(this.createRow(row), {emitEvent: false}));
    this.tableRows = this.rows.getRawValue();
  }

  registerOnChange(fn: (rows: ExternalReferenceRow[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    updateControlEnableState(this.rows, !isDisabled);
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    return this.rows.valid
      ? null
      : this.rows.errors ?? {externalReferences: {messageKey: 'Every external reference needs a label and a reference'}};
  }

  addRow(): void {
    this.rows.push(this.createRow(null));
  }

  protected removeRow(index: number): void {
    this.rows.removeAt(index);
  }

  private createRow(row: ExternalReferenceRow | null): FormGroup<ExternalReferenceRowForm> {
    return new FormGroup<ExternalReferenceRowForm>({
      id: new FormControl(row?.id ?? null),
      label: new FormControl(row?.label ?? '',
        {nonNullable: true, validators: [AceValidators.requiredNotBlank]}),
      externalReference: new FormControl(row?.externalReference ?? '',
        {nonNullable: true, validators: [AceValidators.requiredNotBlank]})
    });
  }
}
